import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { forkJoin, merge, Subject } from "rxjs";
import { AccountRelatedDTO } from "src/app/accounts/Account";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { InstrumentationService } from "../instrumentation.service";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { MatSelectChange } from "@angular/material/select";
import { round } from "lodash";
import { UiService } from "src/app/ui/ui.service";
import { mergeMap, takeUntil } from "rxjs/operators";
import { shortenNumber } from "src/app/util/util.service";

@Component({
  selector: "app-instrumentation",
  templateUrl: "./instrumentation.component.html",
  styleUrls: ["./instrumentation.component.scss"],
})
export class InstrumentationComponent implements OnInit {
  filterForm!: FormGroup;
  baselineOptions: (
    | { value: number; name: string }
    | { value: string; name: string }
  )[];
  accountAppInstruments!: {
    account: AccountRelatedDTO;
    appInstrumentations: any;
  }[];
  accountsAppInstruments!: any[];
  availableInstruments: any;

  faPlus = faPlus;
  accountsWithNoPanel!: AccountRelatedDTO[] | undefined;
  createNewPanelOpen: boolean = false;
  // showSwapPanel: boolean = false;
  panelForm!: FormGroup;
  private onComponentDestroy = new Subject();

  constructor(
    private route: ActivatedRoute,
    private InstrumentationService: InstrumentationService,
    private NotificationService: NotificationService,
    private SessionService: SessionService,
    private Router: Router,
    private uiService: UiService,
  ) {
    this.baselineOptions = [
      {
        value: 7,
        name: "Prior 7 days",
      },
      {
        value: 15,
        name: "Prior 15 days",
      },
      {
        value: 30,
        name: "Prior 30 days",
      },
      {
        value: 45,
        name: "Prior 45 days",
      },
      {
        value: 60,
        name: "Prior 60 days",
      },
      {
        value: 90,
        name: "Prior 90 days",
      },
      {
        value: "industry",
        name: "Industry",
      },
    ];
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(() => {
        this.availableInstruments = this.route.snapshot.data.instruments;
        this.initiateAppInstruments(
          this.route.snapshot.data.accountAppInstruments,
        );
        this.initInstrumentGroups(this.availableInstruments);
      });
    this.filterForm = new FormGroup({
      startDate: new FormControl(this.route.snapshot.data.startDate, [
        Validators.required,
      ]),
      endDate: new FormControl(this.route.snapshot.data.endDate, [
        Validators.required,
      ]),
      baseline: new FormControl(this.route.snapshot.data.compareDays, [
        Validators.required,
      ]),
    });

    this.panelForm = new FormGroup({
      addPanel: new FormControl(),
    });

    this.panelForm.controls.addPanel.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((value) => {
        this.addAccountPanel(value);
        this.panelForm.controls.addPanel.setValue(null, { emitEvent: false });
      });

    this.filterForm.valueChanges.subscribe((change) => {
      this.onDateChanges();
    });
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get startDate() {
    return this.filterForm.controls.startDate.value;
  }

  get endDate() {
    return this.filterForm.controls.endDate.value;
  }

  get baseline() {
    return this.filterForm.controls.baseline.value;
  }

  get baselineFormControl() {
    return this.filterForm.get("baseline") as FormControl;
  }

  get createNewPanelDisabled() {
    return !this.accountsWithNoPanel?.length || this.createNewPanelOpen;
  }

  initInstrumentGroups(availableInstruments: any) {
    const systemInstruments = [
      "last_login",
      "active_flows",
      "total_flows",
      "active_signal",
      "total_signals",
      "last_flow_action",
      "last_broadcast",
      "total_users",
      "registered_user_profiles",
    ];

    const mobileAppsInstruments = [
      "installs",
      "geofence_entries",
      "active_signals",
      "last_quicksignal",
      "last_signal",
      "last_signal_created",
      "location_allowed",
      "push_allowed",
      "location_visits",
      "flow_signals_sent",
      "broadcasts_sent",
    ];

    for (let accountAppInstruments of this.accountsAppInstruments) {
      const instrumentGroups: {
        code: string;
        name: string;
        instruments: any[];
      }[] = [
        {
          code: "system",
          name: "System",
          instruments: [],
        },
        {
          code: "mobile_apps",
          name: "Mobile Apps",
          instruments: [],
        },
      ];

      for (let availableInstrument of availableInstruments) {
        const instrument = { ...availableInstrument };
        instrument.selected = !!accountAppInstruments.appInstrumentations.find(
          (appInstrumentation: any) => {
            return appInstrumentation.instrument_id === instrument.id;
          },
        );

        if (systemInstruments.includes(instrument.code)) {
          instrumentGroups
            .find((instrumentGroup) => instrumentGroup.code === "system")!
            .instruments.push(instrument);
        } else if (mobileAppsInstruments.includes(instrument.code)) {
          instrumentGroups
            .find((instrumentGroup) => instrumentGroup.code === "mobile_apps")!
            .instruments.push(instrument);
        } else {
          console.log("Unkown instrument type", instrument.code);
        }
      }

      accountAppInstruments.instrumentGroups = instrumentGroups;
    }
  }

  initiateAppInstruments(accountsAppInstruments: any) {
    this.accountsAppInstruments = [];

    const currDate = new Date();
    const accountsIDs = this.route.snapshot.queryParams.account;

    //Sort in the order of the query params
    accountsAppInstruments.sort(
      (accountsAppInstrumentA: any, accountsAppInstrumentB: any) => {
        return (
          accountsIDs.indexOf(accountsAppInstrumentA.account.id.toString()) -
          accountsIDs.indexOf(accountsAppInstrumentB.account.id.toString())
        );
      },
    );

    for (let accountAppInstruments of accountsAppInstruments) {
      const accountAppInstrumentsArr: any[] = [];

      for (let accountAppInstrument of accountAppInstruments.appInstrumentations) {
        if (accountAppInstrument.instrument.code === "last_login") {
          this.initLastLogin(accountAppInstrument, currDate);
        } else if (accountAppInstrument.instrument.code === "installs") {
          this.initInstalls(accountAppInstrument);
        } else if (
          accountAppInstrument.instrument.code === "geofence_entries"
        ) {
          this.initGeofenceEntries(accountAppInstrument);
        } else if (accountAppInstrument.instrument.code === "active_signals") {
          this.initActiveSignals(accountAppInstrument);
        } else if (accountAppInstrument.instrument.code === "last_signal") {
          this.initLastSignal(accountAppInstrument, currDate);
        } else if (
          accountAppInstrument.instrument.code === "last_quicksignal"
        ) {
          this.initLastQuickSignal(accountAppInstrument, currDate);
        } else if (
          accountAppInstrument.instrument.code === "last_signal_created"
        ) {
          this.initLastSignalCreated(accountAppInstrument, currDate);
        } else if (
          accountAppInstrument.instrument.code === "location_allowed"
        ) {
          this.initLocationAllowed(accountAppInstrument);
        } else if (accountAppInstrument.instrument.code === "push_allowed") {
          this.initPushAllowed(accountAppInstrument);
        } else if (accountAppInstrument.instrument.code === "location_visits") {
          this.initLocationVisits(accountAppInstrument);
        } else if (
          accountAppInstrument.instrument.code === "flow_signals_sent"
        ) {
          this.initFlowSignalsSent(accountAppInstrument);
        } else if (accountAppInstrument.instrument.code === "broadcasts_sent") {
          this.initBroadcastsSent(accountAppInstrument);
        } else if (
          accountAppInstrument.instrument.code === "registered_user_profiles"
        ) {
          this.initRegisteredUserProfiles(accountAppInstrument);
        }

        accountAppInstrumentsArr.push(accountAppInstrument);
      }
      accountAppInstruments.showSwapPanel = false;
      this.accountsAppInstruments.push(accountAppInstruments);
    }

    this.accountsWithNoPanel =
      this.SessionService.accounts?.filter((account) => {
        const accountHasPanel = !!this.accountsAppInstruments.find(
          (accountAppInstrument) => {
            return accountAppInstrument.account.id === account.id;
          },
        );

        return account.apps.length && !accountHasPanel;
      }) ?? [];

    if (
      !this.route.snapshot.queryParams.account.find(
        (account: string) => account === "all",
      )
    ) {
      this.accountsWithNoPanel!.unshift({
        id: "all",
        name: "All Accounts",
        apps: [{ id: "all" }],
      } as any as AccountRelatedDTO);
    }

    for (let accountAppInstrument of this.accountsAppInstruments) {
      accountAppInstrument.appInstrumentations.sort((a: any, b: any) => {
        return a.sort_order - b.sort_order;
      });
    }

    setTimeout(() => {
      for (let accountAppInstrument of this.accountsAppInstruments) {
        for (let appInstrumentation of accountAppInstrument.appInstrumentations) {
          if (appInstrumentation.showChange) {
            const changeIcon = document.getElementById(
              appInstrumentation.id + "-change-icon",
            );
            if (changeIcon) {
              changeIcon.style.transform =
                "rotate(" + appInstrumentation.rotate + "deg)";
            }
          }
        }
      }
    });
  }

  onDateChanges() {
    if (this.filterForm.valid) {
      // this.reinitInstruments();

      this.Router.navigate([], {
        relativeTo: this.route.parent,
        queryParams: {
          ...this.route.snapshot.queryParams,
          start: this.filterForm.controls.startDate.value.toISOString(),
          end: this.filterForm.controls.endDate.value.toISOString(),
          baseline: this.filterForm.controls.baseline.value,
        },
      });
    }
  }

  initLastLogin(accountAppInstrument: any, currDate: Date) {
    accountAppInstrument.instrument.icon = "last_login";
    if (accountAppInstrument.last_login_at) {
      const lastLogin = new Date(accountAppInstrument.last_login_at);
      accountAppInstrument.value = this.getDateValue(lastLogin);

      const hourDiff = this.hourDifference(currDate, lastLogin);

      if (hourDiff >= 169) {
        accountAppInstrument.color = "bad";
      } else if (hourDiff >= 49) {
        accountAppInstrument.color = "warn";
      } else {
        accountAppInstrument.color = "good";
      }
    } else {
      accountAppInstrument.value = "None";
      accountAppInstrument.color = "bad";
    }
    accountAppInstrument.isDate = true;
  }

  initInstalls(accountAppInstrument: any) {
    accountAppInstrument.instrument.icon = "installs";
    accountAppInstrument.showChange = true;
    accountAppInstrument.value = shortenNumber(accountAppInstrument.installs);

    const isNegativeChange =
      accountAppInstrument.previous_period > accountAppInstrument.installs;

    this.setComparativeChange(
      accountAppInstrument,
      "installs",
      "previous_period",
    );

    if (isNegativeChange || accountAppInstrument.change === 0) {
      accountAppInstrument.color = "bad";
    } else if (accountAppInstrument.change <= 19) {
      accountAppInstrument.color = "warn";
    } else {
      accountAppInstrument.color = "good";
    }

    accountAppInstrument.rotate = this.getComparativeRotation(
      accountAppInstrument.change,
      isNegativeChange,
    );
  }

  initRegisteredUserProfiles(accountAppInstrument: any) {
    accountAppInstrument.instrument.icon = "installs";
    accountAppInstrument.showChange = true;
    accountAppInstrument.value = shortenNumber(
      accountAppInstrument.registered_users,
    );

    const isNegativeChange =
      accountAppInstrument.previous_period >
      accountAppInstrument.registered_users;

    this.setComparativeChange(
      accountAppInstrument,
      "registered_users",
      "previous_period",
    );

    if (isNegativeChange || accountAppInstrument.change === 0) {
      accountAppInstrument.color = "bad";
    } else if (accountAppInstrument.change <= 19) {
      accountAppInstrument.color = "warn";
    } else {
      accountAppInstrument.color = "good";
    }

    accountAppInstrument.rotate = this.getComparativeRotation(
      accountAppInstrument.change,
      isNegativeChange,
    );
  }

  initLocationVisits(accountAppInstrument: any) {
    accountAppInstrument.instrument.icon = "location_allowed";
    accountAppInstrument.showChange = true;
    accountAppInstrument.value = shortenNumber(
      accountAppInstrument.location_visits,
    );

    const isNegativeChange =
      accountAppInstrument.previous_period >
      accountAppInstrument.location_visits;

    this.setComparativeChange(
      accountAppInstrument,
      "location_visits",
      "previous_period",
    );

    if (isNegativeChange || accountAppInstrument.change === 0) {
      accountAppInstrument.color = "bad";
    } else if (accountAppInstrument.change <= 19) {
      accountAppInstrument.color = "warn";
    } else {
      accountAppInstrument.color = "good";
    }

    accountAppInstrument.rotate = this.getComparativeRotation(
      accountAppInstrument.change,
      isNegativeChange,
    );
  }

  initFlowSignalsSent(accountAppInstrument: any) {
    accountAppInstrument.instrument.icon = "last_signal";
    accountAppInstrument.showChange = true;
    accountAppInstrument.value = accountAppInstrument.flow_signals_sent;

    const isNegativeChange =
      accountAppInstrument.previous_period >
      accountAppInstrument.flow_signals_sent;

    this.setComparativeChange(
      accountAppInstrument,
      "flow_signals_sent",
      "previous_period",
    );

    if (isNegativeChange || accountAppInstrument.change === 0) {
      accountAppInstrument.color = "bad";
    } else if (accountAppInstrument.change <= 19) {
      accountAppInstrument.color = "warn";
    } else {
      accountAppInstrument.color = "good";
    }

    accountAppInstrument.rotate = this.getComparativeRotation(
      accountAppInstrument.change,
      isNegativeChange,
    );
  }

  initBroadcastsSent(accountAppInstrument: any) {
    accountAppInstrument.instrument.icon = "last_quicksignal";
    accountAppInstrument.showChange = true;
    accountAppInstrument.value = accountAppInstrument.broadcasts_sent;

    const isNegativeChange =
      accountAppInstrument.previous_period >
      accountAppInstrument.broadcasts_sent;

    this.setComparativeChange(
      accountAppInstrument,
      "broadcasts_sent",
      "previous_period",
    );

    if (isNegativeChange || accountAppInstrument.change === 0) {
      accountAppInstrument.color = "bad";
    } else if (accountAppInstrument.change <= 19) {
      accountAppInstrument.color = "warn";
    } else {
      accountAppInstrument.color = "good";
    }

    accountAppInstrument.rotate = this.getComparativeRotation(
      accountAppInstrument.change,
      isNegativeChange,
    );
  }

  initGeofenceEntries(accountAppInstrument: any) {
    accountAppInstrument.instrument.icon = "geofence";
    accountAppInstrument.showChange = true;
    accountAppInstrument.value = shortenNumber(
      accountAppInstrument.geofence_entries,
    );

    const isNegativeChange =
      accountAppInstrument.previous_period >
      accountAppInstrument.geofence_entries;

    this.setComparativeChange(
      accountAppInstrument,
      "geofence_entries",
      "previous_period",
    );

    if (isNegativeChange || accountAppInstrument.change === 0) {
      accountAppInstrument.color = "bad";
    } else if (accountAppInstrument.change <= 9) {
      accountAppInstrument.color = "warn";
    } else {
      accountAppInstrument.color = "good";
    }

    accountAppInstrument.rotate = this.getComparativeRotation(
      accountAppInstrument.change,
      isNegativeChange,
    );
  }

  initActiveSignals(accountAppInstrument: any) {
    accountAppInstrument.instrument.icon = "last_signal";
    accountAppInstrument.value = accountAppInstrument.active_signals;

    if (accountAppInstrument.value < 4) {
      accountAppInstrument.color = "bad";
    } else if (accountAppInstrument.value < 8) {
      accountAppInstrument.color = "warn";
    } else {
      accountAppInstrument.color = "good";
    }
  }

  initLastSignal(accountAppInstrument: any, currDate: Date) {
    accountAppInstrument.instrument.icon = "last_signal";
    if (accountAppInstrument.last_signal) {
      const lastSignal = new Date(accountAppInstrument.last_signal);

      accountAppInstrument.value = this.getDateValue(lastSignal);

      const hourDiff = this.hourDifference(currDate, lastSignal);

      if (hourDiff >= 97) {
        accountAppInstrument.color = "bad";
      } else if (hourDiff >= 49) {
        accountAppInstrument.color = "warn";
      } else {
        accountAppInstrument.color = "good";
      }
    } else {
      accountAppInstrument.value = "None";
      accountAppInstrument.color = "bad";
    }

    accountAppInstrument.isDate = true;
  }

  initLastQuickSignal(accountAppInstrument: any, currDate: Date) {
    accountAppInstrument.instrument.icon = "last_quicksignal";
    if (accountAppInstrument.last_quicksignal) {
      const lastQuickSignal = new Date(accountAppInstrument.last_quicksignal);

      accountAppInstrument.value = this.getDateValue(lastQuickSignal);

      const dateDiff = this.dateDifference(currDate, lastQuickSignal);

      if (dateDiff >= 31) {
        accountAppInstrument.color = "bad";
      } else if (dateDiff >= 15) {
        accountAppInstrument.color = "warn";
      } else {
        accountAppInstrument.color = "good";
      }
    } else {
      accountAppInstrument.value = "None";
      accountAppInstrument.color = "bad";
    }

    accountAppInstrument.isDate = true;
  }

  initLastSignalCreated(accountAppInstrument: any, currDate: Date) {
    accountAppInstrument.instrument.icon = "last_signal";

    if (accountAppInstrument.last_signal_created) {
      const lastSignalCreated = new Date(
        accountAppInstrument.last_signal_created,
      );

      accountAppInstrument.value = this.getDateValue(lastSignalCreated);

      const dateDiff = this.dateDifference(currDate, lastSignalCreated);

      if (dateDiff >= 31) {
        accountAppInstrument.color = "bad";
      } else if (dateDiff >= 15) {
        accountAppInstrument.color = "warn";
      } else {
        accountAppInstrument.color = "good";
      }
    } else {
      accountAppInstrument.value = "None";
      accountAppInstrument.color = "bad";
    }

    accountAppInstrument.isDate = true;
  }

  initLocationAllowed(accountAppInstrument: any) {
    accountAppInstrument.instrument.icon = "location_allowed";

    accountAppInstrument.value =
      round(accountAppInstrument.location_allowed, 2) * 100;

    if (accountAppInstrument.value < 39) {
      accountAppInstrument.color = "bad";
    } else if (accountAppInstrument.value < 59) {
      accountAppInstrument.color = "warn";
    } else {
      accountAppInstrument.color = "good";
    }

    accountAppInstrument.value =
      accountAppInstrument.value !== undefined &&
      accountAppInstrument.value !== null &&
      !isNaN(accountAppInstrument.value)
        ? `${accountAppInstrument.value}%`
        : accountAppInstrument.value;
  }

  initPushAllowed(accountAppInstrument: any) {
    accountAppInstrument.instrument.icon = "push_allowed";
    accountAppInstrument.value = round(
      Number.parseFloat(accountAppInstrument.push_allowed) * 100,
    );

    if (accountAppInstrument.value < 49) {
      accountAppInstrument.color = "bad";
    } else if (accountAppInstrument.value < 69) {
      accountAppInstrument.color = "warn";
    } else {
      accountAppInstrument.color = "good";
    }

    accountAppInstrument.value =
      accountAppInstrument.value !== undefined &&
      accountAppInstrument.value !== null &&
      !isNaN(accountAppInstrument.value)
        ? `${accountAppInstrument.value}%`
        : accountAppInstrument.value;
  }

  dateDifference(date1: Date, date2: Date) {
    return (date2.getTime() - date1.getTime()) / (1000 * 60 * 60 * 24);
  }

  hourDifference(date1: Date, date2: Date) {
    return (date2.getTime() - date1.getTime()) / (1000 * 60 * 60);
  }

  getDateValue(date: Date) {
    return (
      this.getDay(date.getDate()) +
      " " +
      this.getMonth(date.getMonth()) +
      " " +
      date.getFullYear()
    );
  }

  getDay(dayNumber: number) {
    let day = dayNumber.toString();
    if (day.length === 1) {
      day = "0" + day;
    }
    return day;
  }

  getMonth(monthNumber: number) {
    const month = monthNumber + 1;
    if (month === 1) {
      return "Jan";
    }
    if (month === 2) {
      return "Feb";
    }
    if (month === 3) {
      return "Mar";
    }
    if (month === 4) {
      return "Apr";
    }
    if (month === 5) {
      return "May";
    }
    if (month === 6) {
      return "Jun";
    }
    if (month === 7) {
      return "Jul";
    }
    if (month === 8) {
      return "Aug";
    }
    if (month === 9) {
      return "Sep";
    }
    if (month === 10) {
      return "Oct";
    }
    if (month === 11) {
      return "Nov";
    }
    if (month === 12) {
      return "Dec";
    }
    return "";
  }

  getComparativeRotation(value: number, isNegative: boolean) {
    let multiplier;
    if (isNegative) {
      multiplier = 1;
    } else {
      multiplier = -1;
    }

    if (value >= 25) {
      return multiplier * 90;
    } else if (value >= 15) {
      return multiplier * 60;
    } else if (value >= 10) {
      return multiplier * 30;
    } else if (value >= 5) {
      return multiplier * 20;
    } else if (value >= 1) {
      return multiplier * 10;
    } else {
      return 0;
    }
  }

  setComparativeChange(
    accountAppInstrument: any,
    valueKey: string,
    previousKey: string,
  ) {
    if (accountAppInstrument[previousKey] === 0) {
      if (accountAppInstrument[valueKey] === 0) {
        accountAppInstrument.change = 0;
      } else {
        accountAppInstrument.change = 100;
      }
    } else {
      accountAppInstrument.change =
        Math.round(
          (Math.abs(
            (accountAppInstrument[valueKey] -
              accountAppInstrument[previousKey]) /
              accountAppInstrument[previousKey],
          ) *
            100 +
            Number.EPSILON) *
            100,
        ) / 100;
    }
  }

  createNewPanel() {
    this.createNewPanelOpen = true;
  }

  closePanel(accountAppInstrument: any) {
    this.Router.navigate([], {
      relativeTo: this.route.parent,
      queryParams: {
        ...this.route.snapshot.queryParams,
        account: this.route.snapshot.queryParams.account.filter(
          (accountID: string) => {
            if (accountID === "all") {
              return accountID !== accountAppInstrument.account.id;
            }
            return (
              Number.parseInt(accountID) !== accountAppInstrument.account.id
            );
          },
        ),
      },
    });
  }

  showSwapPanel(accountAppInstrument: any) {
    accountAppInstrument.showSwapPanel = true;
  }

  cancelSwapPanel(accountAppInstrument: any) {
    accountAppInstrument.showSwapPanel = false;
  }

  swapPanel(change: MatSelectChange, accountAppInstrument: any) {
    const accountArr = [...this.route.snapshot.queryParams.account];
    const currIndex = accountArr.indexOf(
      accountAppInstrument.account.id.toString(),
    );
    accountArr[currIndex] = change.value.id;

    accountAppInstrument.showSwapPanel = false;

    this.Router.navigate(["instrumentation", "view"], {
      queryParams: { account: [...accountArr] },
    });
  }

  addAccountPanel(account: AccountRelatedDTO) {
    this.Router.navigate([], {
      relativeTo: this.route.parent,
      queryParams: {
        ...this.route.snapshot.queryParams,
        account: [...this.route.snapshot.queryParams.account, account.id],
        start: this.filterForm.controls.startDate.value.toISOString(),
        end: this.filterForm.controls.endDate.value.toISOString(),
      },
    });

    this.createNewPanelOpen = false;
  }

  removeNewPanel() {
    this.createNewPanelOpen = false;
  }

  reinitInstruments() {
    this.endDate.setHours(23, 59, 59);

    const accounts = this.route.snapshot.queryParams.account.map(
      (accountID: string) => {
        if (accountID === "all") {
          return { id: "all", apps: [{ id: "all" }] };
        }
        return this.SessionService.accounts!.find((account) => {
          return account.id === Number.parseInt(accountID);
        });
      },
    );

    const accountAppInstrumentsObsArr = accounts.map((account: any) => {
      return this.InstrumentationService.getAccountAppInstrumentsObs(
        account,
        this.availableInstruments,
        this.startDate,
        this.endDate,
        this.baseline,
      );
    });

    return this.uiService
      .waitForEvent(forkJoin(accountAppInstrumentsObsArr))
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (appInstrumentations: any) => {
          this.initiateAppInstruments(appInstrumentations);
          this.initInstrumentGroups(this.availableInstruments);
        },
        () => {
          this.NotificationService.openErrorSnackBar(
            "Error retrieving instruments",
          );
        },
      );
  }

  swapInstrument(
    newInstrument: any,
    oldAppInstrument: any,
    accountAppInstrument: any,
  ) {
    this.uiService
      .waitForEvent(
        this.InstrumentationService.swapInstruments(
          accountAppInstrument.account.id,
          accountAppInstrument.account.apps[0].id,
          newInstrument,
          oldAppInstrument,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        () => {
          this.NotificationService.openSuccessSnackBar("Instrument swapped");
          this.reinitInstruments();
        },
        () => {
          this.NotificationService.openErrorSnackBar(
            "Error swapping instrument",
          );
        },
      );
  }

  removeInstrument(appInstrument: any, accountAppInstruments: any) {
    this.uiService
      .waitForEvent(
        this.InstrumentationService.deleteAppInstrument(
          accountAppInstruments.account.id,
          accountAppInstruments.account.apps[0].id,
          appInstrument.id,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        () => {
          this.NotificationService.openSuccessSnackBar("Instrument removed");
          this.reinitInstruments();
        },
        () => {
          this.NotificationService.openErrorSnackBar(
            "Error removing instrument",
          );
        },
      );
  }

  addInstrument(instrument: any, accountAppInstrument: any) {
    const newAppInstrumentDTO = {
      instrument_id: instrument.id,
      sort_order: accountAppInstrument.appInstrumentations.length
        ? accountAppInstrument.appInstrumentations[
            accountAppInstrument.appInstrumentations.length - 1
          ].sort_order + 1
        : 0,
    };
    this.uiService
      .waitForEvent(
        this.InstrumentationService.createAppInstrument(
          accountAppInstrument.account.id,
          accountAppInstrument.account.apps[0].id,
          newAppInstrumentDTO,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        () => {
          return this.reinitInstruments();
        },
        () => {
          this.NotificationService.openErrorSnackBar("Error adding instrument");
        },
      );
  }
}
